// src/pages/MeetingRoom/hooks/useBreakoutRoom.js
import { useEffect, useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { socketService } from "@/Services/socketService";
import useBreakoutStore from "@/store/useBreakoutStore";
import {
    createBreakout,
    inviteToBreakout,
    respondToBreakoutInvite,
    leaveBreakout,
    endBreakout,
    getMyActiveBreakout,
} from "@/Services/BreakoutMeetingServices";

export function useBreakoutRoom({ sessionId, myUserId, onBreakoutStart, onBreakoutEnd }) {
    const breakoutStore = useBreakoutStore();
    const mountedRef = useRef(true);
    const endedBreakoutsRef = useRef(new Set()); // ✅ FIX 4: track already-ended breakouts
    const didCheckRef = useRef(false);      // ✅ FIX 4: only run reconnect check once

    // ==================== SOCKET LISTENERS ====================

    useEffect(() => {
        mountedRef.current = true;

        const handleBreakoutInvite = (data) => {
            console.log("📩 Breakout invite received:", data);
            breakoutStore.setPendingInvite(data);
        };

        const handleInviteRejected = (data) => {
            if (breakoutStore.isOwner) {
                toast.error(`${data.userName || "Someone"} declined the invite`);
            }
        };

        const handleParticipantJoined = (data) => {
            breakoutStore.addParticipant({
                userId: data.userId,
                name: data.userName,
                image: data.image || null,
                isOwner: false,
            });
            toast.success(`${data.userName} joined the private meeting`);
        };

        const handleParticipantLeft = (data) => {
            breakoutStore.removeParticipant(data.userId);
        };

        const handleBreakoutEnded = (data) => {
            console.log("🛑 Breakout ended:", data);

            // ✅ FIX 4: deduplicate - this event fires multiple times
            if (endedBreakoutsRef.current.has(data.breakoutId)) return;
            endedBreakoutsRef.current.add(data.breakoutId);

            // ✅ FIX 4: Always clear localStorage regardless of isActive state
            localStorage.removeItem("activeBreakoutId");
            localStorage.removeItem("activeSessionId");

            if (!breakoutStore.isActive) return;

            const reasonMessages = {
                owner_ended: "The private meeting has been ended by the host.",
                time_expired: "The private meeting time limit has been reached.",
                owner_disconnected: "The private meeting host lost connection and the meeting ended.",
            };

            toast.error(reasonMessages[data.reason] || "The private meeting has ended.");

            breakoutStore.clearBreakout();
            onBreakoutEnd?.();
        };

        const handleOwnerDisconnected = (data) => {
            breakoutStore.setOwnerDisconnected(true, data.gracePeriodSeconds || 60);
            toast.warning(
                data.message || "The meeting owner has lost connection. Waiting for them to reconnect...",
                { duration: 5000 }
            );
        };

        const handleOwnerReconnected = (data) => {
            breakoutStore.setOwnerDisconnected(false);
            toast.success(data.message || "The meeting owner has reconnected.");
        };

        const handleTimeWarning = (data) => {
            toast.warning(
                `⚠️ This private meeting ends in ${data.minutesRemaining} minutes`,
                { duration: 5000 }
            );
        };

        socketService.onBreakoutInvite(handleBreakoutInvite);
        socketService.onBreakoutInviteRejected(handleInviteRejected);
        socketService.onBreakoutParticipantJoined(handleParticipantJoined);
        socketService.onBreakoutParticipantLeft(handleParticipantLeft);
        socketService.onBreakoutEnded(handleBreakoutEnded);
        socketService.onBreakoutOwnerDisconnected(handleOwnerDisconnected);
        socketService.onBreakoutOwnerReconnected(handleOwnerReconnected);
        socketService.onBreakoutTimeWarning(handleTimeWarning);

        return () => {
            mountedRef.current = false;
            socketService.offBreakoutInvite(handleBreakoutInvite);
            socketService.offBreakoutInviteRejected(handleInviteRejected);
            socketService.offBreakoutParticipantJoined(handleParticipantJoined);
            socketService.offBreakoutParticipantLeft(handleParticipantLeft);
            socketService.offBreakoutEnded(handleBreakoutEnded);
            socketService.offBreakoutOwnerDisconnected(handleOwnerDisconnected);
            socketService.offBreakoutOwnerReconnected(handleOwnerReconnected);
            socketService.offBreakoutTimeWarning(handleTimeWarning);
        };
    }, [breakoutStore, onBreakoutEnd]);

    // ==================== API ACTIONS ====================

    const handleCreateBreakout = useCallback(
        async (timeLimitMinutes) => {
            if (!sessionId) {
                toast.error("Missing session ID");
                return { success: false };
            }

            // ✅ FIX 4: Reset ended set so new breakout can be created
            endedBreakoutsRef.current = new Set();

            try {
                const response = await createBreakout(sessionId, timeLimitMinutes);

                if (!response.success) {
                    toast.error(response.message || "Failed to create breakout");
                    return { success: false };
                }

                const {
                    breakoutId,
                    livekitToken,
                    livekitRoomName,
                    livekitServerUrl,
                    expiresAt,
                } = response.data;

                breakoutStore.setBreakout({
                    breakoutId,
                    livekitToken,
                    livekitRoomName,
                    livekitServerUrl,
                    expiresAt,
                    timeLimitMinutes,
                    sessionId,
                    isOwner: true,
                });

                // Add self as first participant
                breakoutStore.setParticipants([
                    {
                        userId: myUserId,
                        name: localStorage.getItem("fullName") || "You",
                        image: null,
                        isOwner: true,
                    },
                ]);

                localStorage.setItem("activeBreakoutId", breakoutId);
                localStorage.setItem("activeSessionId", sessionId);

                socketService.registerBreakoutUser(myUserId, sessionId, breakoutId);

                toast.success("Private meeting created!");
                onBreakoutStart?.({ ...response.data, isOwner: true });

                return { success: true, data: response.data };
            } catch (error) {
                // ✅ Handle 409 — already in a breakout
                if (error?.response?.status === 409) {
                    const existingId = error?.response?.data?.data?.breakoutId;
                    toast.error(
                        existingId
                            ? "You are already in a private meeting."
                            : error?.response?.data?.message || "Already in a breakout"
                    );
                    return { success: false, code: "USER_IN_PRIVATE_BREAKOUT" };
                }

                const msg = error?.response?.data?.message || error.message || "Failed to create breakout";
                toast.error(msg);
                return { success: false, error: msg };
            }
        },
        [sessionId, myUserId, breakoutStore, onBreakoutStart]
    );

    const handleInviteParticipants = useCallback(
        async (inviteeIds) => {
            const { breakoutId } = breakoutStore;

            if (!breakoutId) {
                toast.error("No active breakout to invite to");
                return { success: false };
            }

            if (!inviteeIds || inviteeIds.length === 0) {
                toast.error("No participants selected");
                return { success: false };
            }

            try {
                const response = await inviteToBreakout(breakoutId, inviteeIds);

                if (!response.success) {
                    toast.error(response.message || "Failed to send invites");
                    return { success: false };
                }

                toast.success(`Invites sent to ${inviteeIds.length} participant(s)`);
                return { success: true, data: response.data };
            } catch (error) {
                const msg = error?.response?.data?.message || error.message || "Failed to send invites";
                toast.error(msg);
                return { success: false, error: msg };
            }
        },
        [breakoutStore]
    );

    const handleRespondToInvite = useCallback(
        async (breakoutId, accepted) => {
            try {
                const response = await respondToBreakoutInvite(breakoutId, accepted);

                if (!response.success) {
                    toast.error(response.message || "Failed to respond to invite");
                    return { success: false };
                }

                if (accepted) {
                    const {
                        livekitToken,
                        livekitRoomName,
                        livekitServerUrl,
                        expiresAt,
                        timeLimitMinutes,
                    } = response.data;

                    breakoutStore.setBreakout({
                        breakoutId,
                        livekitToken,
                        livekitRoomName,
                        livekitServerUrl,
                        expiresAt,
                        timeLimitMinutes,
                        sessionId,
                        isOwner: false,
                    });

                    localStorage.setItem("activeBreakoutId", breakoutId);
                    localStorage.setItem("activeSessionId", sessionId);

                    socketService.registerBreakoutUser(myUserId, sessionId, breakoutId);

                    // ✅ FIX 2: clear pending invite after successful accept
                    breakoutStore.clearPendingInvite();

                    toast.success("Joined private meeting!");
                    onBreakoutStart?.({ ...response.data, isOwner: false });

                    return { success: true, data: response.data };
                } else {
                    breakoutStore.clearPendingInvite();
                    toast("Invite declined");
                    return { success: true };
                }
            } catch (error) {
                const msg = error?.response?.data?.message || error.message || "Failed to respond";
                toast.error(msg);
                return { success: false, error: msg };
            }
        },
        [sessionId, myUserId, breakoutStore, onBreakoutStart]
    );

    const handleLeaveBreakout = useCallback(async () => {
        const { breakoutId } = breakoutStore;
        if (!breakoutId) return { success: false };

        try {
            await leaveBreakout(breakoutId);
        } catch (error) {
            console.warn("Leave API error (ignoring):", error?.message);
        }

        // ✅ FIX 4: Mark as ended so reconnect logic ignores it
        endedBreakoutsRef.current.add(breakoutId);

        // ✅ Always clear state
        breakoutStore.clearBreakout();
        localStorage.removeItem("activeBreakoutId");
        localStorage.removeItem("activeSessionId");

        toast.success("Left private meeting");
        onBreakoutEnd?.();

        return { success: true };
    }, [breakoutStore, onBreakoutEnd]);

    const handleEndBreakout = useCallback(async () => {
        const { breakoutId, isOwner } = breakoutStore;
        if (!breakoutId) return { success: false };

        if (!isOwner) {
            toast.error("Only the owner can end the meeting");
            return { success: false };
        }

        try {
            await endBreakout(breakoutId);
        } catch (error) {
            console.warn("End API error (ignoring):", error?.message);
        }

        // ✅ FIX 4: Mark as ended
        endedBreakoutsRef.current.add(breakoutId);

        breakoutStore.clearBreakout();
        localStorage.removeItem("activeBreakoutId");
        localStorage.removeItem("activeSessionId");

        toast.success("Private meeting ended");
        onBreakoutEnd?.();

        return { success: true };
    }, [breakoutStore, onBreakoutEnd]);

    // ==================== RECONNECT ON MOUNT ====================

    const checkForActiveBreakout = useCallback(async () => {
        // ✅ FIX 4: Only run once per mount
        if (didCheckRef.current) return;
        didCheckRef.current = true;

        const savedBreakoutId = localStorage.getItem("activeBreakoutId");
        const savedSessionId = localStorage.getItem("activeSessionId");

        if (!savedBreakoutId || !savedSessionId) return;

        // ✅ FIX 4: Don't reconnect to a breakout we already know ended
        if (endedBreakoutsRef.current.has(savedBreakoutId)) {
            localStorage.removeItem("activeBreakoutId");
            localStorage.removeItem("activeSessionId");
            return;
        }

        // Session mismatch — clear stale data
        if (savedSessionId !== sessionId) {
            localStorage.removeItem("activeBreakoutId");
            localStorage.removeItem("activeSessionId");
            return;
        }

        try {
            const response = await getMyActiveBreakout(sessionId);

            if (!mountedRef.current) return;

            if (!response.success || !response.data?.activeBreakout) {
                localStorage.removeItem("activeBreakoutId");
                localStorage.removeItem("activeSessionId");
                return;
            }

            const { activeBreakout } = response.data;

            // ✅ FIX 4: Double-check the returned breakoutId matches saved one
            if (activeBreakout.breakoutId !== savedBreakoutId) {
                localStorage.removeItem("activeBreakoutId");
                localStorage.removeItem("activeSessionId");
                return;
            }

            breakoutStore.setBreakout({
                breakoutId: activeBreakout.breakoutId,
                livekitToken: activeBreakout.livekitToken,
                livekitRoomName: activeBreakout.livekitRoomName,
                livekitServerUrl: activeBreakout.livekitServerUrl,
                expiresAt: activeBreakout.expiresAt,
                timeLimitMinutes: activeBreakout.timeLimitMinutes,
                sessionId,
                isOwner: activeBreakout.isOwner,
            });

            socketService.registerBreakoutUser(
                myUserId,
                sessionId,
                activeBreakout.breakoutId
            );

            onBreakoutStart?.(activeBreakout);

            console.log("✅ Reconnected to active breakout:", activeBreakout.breakoutId);
        } catch (error) {
            console.error("Failed to reconnect to breakout:", error);
            localStorage.removeItem("activeBreakoutId");
            localStorage.removeItem("activeSessionId");
        }
    }, [sessionId, myUserId, breakoutStore, onBreakoutStart]);

    useEffect(() => {
        if (sessionId && myUserId) {
            checkForActiveBreakout();
        }
    }, [sessionId, myUserId, checkForActiveBreakout]);

    // ==================== RETURN ====================

    return {
        breakoutId: breakoutStore.breakoutId,
        isActive: breakoutStore.isActive,
        isOwner: breakoutStore.isOwner,
        participants: breakoutStore.participants,
        pendingInvite: breakoutStore.pendingInvite,
        ownerDisconnected: breakoutStore.ownerDisconnected,
        gracePeriodSeconds: breakoutStore.gracePeriodSeconds,
        expiresAt: breakoutStore.expiresAt,
        timeLimitMinutes: breakoutStore.timeLimitMinutes,
        livekitToken: breakoutStore.livekitToken,
        livekitServerUrl: breakoutStore.livekitServerUrl,

        createBreakout: handleCreateBreakout,
        inviteParticipants: handleInviteParticipants,
        respondToInvite: handleRespondToInvite,
        leaveBreakout: handleLeaveBreakout,
        endBreakout: handleEndBreakout,
        clearPendingInvite: breakoutStore.clearPendingInvite,
    };
}